import { useEffect, useRef, useState } from 'react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { type DataTablePage } from './DataTablePage';
import { type ListData } from '@/app/models/ListData';
import { parseAsBase64 } from '../../../utils/UrlParser';

export const getDataTableQueryKey = (tableKey?: string, pageIndex?: number) => {
  const tableQueryKey = ['dataTableQuery', tableKey] as const;
  if (pageIndex === undefined) return tableQueryKey;
  return [...tableQueryKey, { pageIndex }] as const;
};

export const getDataTablePage = <T extends object>(response: ListData<T>): DataTablePage<T> => ({
  rows: response.content,
  totalItemCount: response.total_elements,
  totalPageCount: response.total_pages,
  pageItemCount: response.content.length,
  exportItemCount: response.total_elements,
  additionalContent: response.additional_content,
});

/**
 * Página atual e termo de busca na URL, para que recarregar a página ou voltar de
 * uma tela de detalhe caia na mesma listagem — e para que o link seja compartilhável.
 *
 * O estado React é a fonte da verdade e a URL é espelho: lida uma vez na montagem,
 * escrita a cada mudança, nunca relida. A escrita do nuqs é assíncrona e reconciliada
 * contra a URL, então ler dela de volta deixaria o `<input>` de busca e os controles de
 * paginação fora de sincronia com o clique/tecla que acabou de acontecer. Como o nuqs
 * grava com `history: 'replace'`, não há entradas de histórico para reagir mesmo.
 *
 * Só espelha quando a tabela declara `tableKey`: sem ele a chave seria o hash das
 * colunas gerado por `generateTableKey`, que muda sozinho quando alguém mexe nas
 * colunas e invalidaria links antigos em silêncio.
 *
 * Tamanho de página e ordenação continuam no localStorage: são preferência do
 * usuário (grudenta entre visitas), não estado da tela.
 */
export const useUrlTableState = (tableKey?: string) => {
  const [urlPage, setUrlPage] = useQueryState(`${tableKey ?? ''}_page`, parseAsInteger.withDefault(0));
  const [urlSearch, setUrlSearch] = useQueryState(`${tableKey ?? ''}_q`, parseAsString.withDefault(''));

  const [page, setPage] = useState(() => (tableKey ? urlPage : 0));
  const [search, setSearch] = useState(() => (tableKey ? urlSearch : ''));
  const mirrored = useRef({ page, search });

  useEffect(() => {
    if (!tableKey) return;

    // `|| null` remove o parâmetro no valor default, mantendo a URL limpa na página 1.
    if (mirrored.current.page !== page) {
      mirrored.current.page = page;
      void setUrlPage(page || null);
    }
    if (mirrored.current.search !== search) {
      mirrored.current.search = search;
      void setUrlSearch(search || null);
    }
  }, [page, search, tableKey, setUrlPage, setUrlSearch]);

  return { page, setPage, search, setSearch };
};

export const useUrlFilter = <T>(key: string) => {
  const parser = parseAsBase64<T>();
  const [value, setValue] = useQueryState<T>(`${key}_f`, parser);
  return [value, setValue] as const;
};

export const generateUrlFilter = <T>(filter: T) => {
  const parser = parseAsBase64<T>();
  return parser.serialize(filter);
};

export const generateUrlFilterParam = <T>(filter: T, key: string) => {
  const parser = parseAsBase64<T>();
  return { [`${key}_f`]: parser.serialize(filter) };
};
